import React, { useCallback, useState, useMemo } from 'react';
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment';
import { print } from 'graphql/language/printer';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import { changeOfSupplierSchema, changeOfSupplierUISchema } from '../../schemas/changeOfSupplierSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert/ExpiringAlert';
import { changeOfSupplierBPQuery, organizationsQuery } from '../../../../queries/Queries';

/**
 * Adds the specified number of working days (Mon-Fri) to a date.
 */
const addWorkingDays = (startDate, days) => {
  let current = moment(startDate).startOf('day');
  let added = 0;
  while (added < days) {
    current.add(1, 'days');
    if (current.isoWeekday() <= 5) {
      added++;
    }
  }
  return current;
};

/**
 * Checks if a date is at least `minDays` working days in the future from today.
 */
const isMinWorkingDaysInFuture = (date, minDays) => {
  const target = moment(date).startOf('day');
  const minDate = addWorkingDays(moment(), minDays);
  return target.isSameOrAfter(minDate);
};

/**
 * Derives domestic_indicator from meter point marketSector.
 * "D" (Domestic) → true, "I" (Industrial) → false.
 */
const getDomesticIndicator = (marketSector) => {
  if (marketSector === 'D') return true;
  if (marketSector === 'I') return false;
  return false;
};

/**
 * Derives is_initial_registration from meter point supply history.
 * Most recent entry by dateOfAction: if isStopsupply=false (Supply=Y) → false,
 * if isStopsupply=true (Supply=N) → true.
 */
const getIsInitialRegistration = (mDMEstateSupplyList) => {
  if (!mDMEstateSupplyList || mDMEstateSupplyList.length === 0) {
    return true;
  }

  const sortedHistory = [...mDMEstateSupplyList].sort((a, b) => {
    const dateA = a.dateOfAction ? new Date(a.dateOfAction) : new Date(0);
    const dateB = b.dateOfAction ? new Date(b.dateOfAction) : new Date(0);
    return dateB - dateA;
  });

  const mostRecent = sortedHistory[0];
  // isStopsupply=false means Supply=Y → is_initial_registration=false
  // isStopsupply=true means Supply=N → is_initial_registration=true
  return mostRecent.isStopsupply === true;
};

/**
 * Finds the primary contact from BP contacts list.
 * Looks for contact with isMdmIsowner=true (Primary meter point role).
 */
const findPrimaryContact = (contactList) => {
  if (!contactList || contactList.length === 0) return null;

  // Try to find the owner/primary contact
  const primary = contactList.find((c) => c.user && c.user.isMdmIsowner);
  if (primary) return primary.user;

  // Fallback to first contact
  return contactList[0] && contactList[0].user ? contactList[0].user : null;
};

const extractPostcode = (addressText) => {
  if (!addressText || typeof addressText !== 'string') return null;
  const match = addressText.match(/([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/i);
  return match ? match[1].toUpperCase().replace(/\s+/g, ' ').trim() : null;
};

/**
 * Maps BP contact address from 360 to mailing_address fields.
 * Address mapping follows the specification from 360 → Business Partner → Contact Address.
 */
const mapContactAddress = (locationAddress) => {
  if (!locationAddress) {
    return {
      mailing_address_1: null,
      mailing_address_2: null,
      mailing_address_3: null,
      mailing_address_4: null,
      mailing_address_5: null,
      mailing_address_6: null,
      mailing_address_7: null,
      mailing_address_8: null,
      mailing_address_9: null,
      mailing_address_postcode: null,
    };
  }

  const fullAddress = locationAddress.addressLine1 || '';
  const postcode = extractPostcode(fullAddress);

  const addressParts = fullAddress
    .split(',')
    .map((part) => {
      if (!postcode) return part;
      // Remove the postcode from address lines so it is only sent in mailing_address_postcode.
      return part.replace(new RegExp(postcode.replace(/\s+/g, '\\s*'), 'i'), '');
    })
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  return {
    mailing_address_1: addressParts[0] || null,
    mailing_address_2: addressParts[1] || null,
    mailing_address_3: addressParts[2] || null,
    mailing_address_4: addressParts[3] || null,
    mailing_address_5: addressParts[4] || null,
    mailing_address_6: addressParts[5] || null,
    mailing_address_7: addressParts[6] || null,
    mailing_address_8: addressParts[7] || null,
    mailing_address_9: addressParts[8] || null,
    mailing_address_postcode: postcode,
  };
};

/**
 * Executes a GraphQL query via HTTP POST to /graphql endpoint.
 */
const executeGraphQLQuery = async (query, variables = {}) => {
  try {
    const normalizedQuery = typeof query === 'string' ? query : print(query);

    const response = await axios.post(
      '/graphql',
      {
        query: normalizedQuery,
        variables,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.data && response.data.errors && response.data.errors.length > 0) {
      throw new Error(response.data.errors[0].message || 'GraphQL query failed');
    }

    return response.data.data || {};
  } catch (error) {
    console.error('GraphQL query error:', error);
    throw error;
  }
};

const ChangeOfSupplierForm = ({ onClose, initialData }) => {
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initialData || {});
  const [selectedMeterPoint, setSelectedMeterPoint] = useState(null);

  const handleChange = useCallback(
    ({ formData }) => {
      updateFormContext(formData);
    },
    [updateFormContext],
  );

  const handleMeterPointSelect = useCallback((meterPoint) => {
    setSelectedMeterPoint(meterPoint);
  }, []);

  const domesticIndicator = useMemo(() => {
    if (!selectedMeterPoint) return null;
    return getDomesticIndicator(selectedMeterPoint.marketSector);
  }, [selectedMeterPoint]);

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      switch (error.property) {
        case '.mpan_core':
          return { ...error, message: 'MPAN Core is a required property' };
        case '.supply_start_date':
          return { ...error, message: 'Supply Start Date is a required property' };
        case '.metering_service_mpid':
          return { ...error, message: 'Metering Service MPID is a required property' };
        case '.data_service_mpid':
          return { ...error, message: 'Data Service MPID is a required property' };
        case '.consent_granularity':
          return { ...error, message: 'Consent Granularity is a required property' };
        case '.fall_back_read_frequency':
          return { ...error, message: 'Fall Back Read Frequency is a required property' };
        default:
          return error;
      }
    });
  }, []);

  const customValidate = useCallback(
    (formData, errors) => {
      if (formData.supply_start_date) {
        const minDays = domesticIndicator === true ? 1 : 2;
        if (!isMinWorkingDaysInFuture(formData.supply_start_date, minDays)) {
          const label = domesticIndicator === true ? '1 working day' : '2 working days';
          errors.supply_start_date.addError(`Supply Start Date must be at least ${label} in the future`);
        }
      }

      if (!formData.mpan_core) {
        errors.mpan_core.addError('Please select an MPAN from the dropdown');
      }

      return errors;
    },
    [domesticIndicator],
  );

  /**
   * Fetches meter point data directly from OB endpoint by MPAN name.
   * This is used as a diagnostic step to confirm MPAN visibility in 360.
   */
  const fetchMeterPointData = useCallback(async (mpanName) => {
    const query = `
      query OBmeterPointByName($where: String!) {
        OB_meterPoint(offset: 1, startRow: 0, where: $where, orderBy: "name") {
          id
          name
          marketSector
          mDMMeterPointStatus
          mDMEstateSupplyList {
            dateOfAction
            isStopsupply
          }
        }
      }
    `;

    try {
      const data = await executeGraphQLQuery(query, {
        where: `name = '${mpanName}'`,
      });

      const meterPoint = data && data.OB_meterPoint && data.OB_meterPoint.length > 0
        ? data.OB_meterPoint[0]
        : null;

      return meterPoint;
    } catch (error) {
      console.error('Failed to fetch meter point data:', error);
      return null;
    }
  }, []);

  /**
   * Fetches business partner data from 360 for the given MPAN.
   */
  const fetchBPData = useCallback(async (mpanName) => {
    const query = changeOfSupplierBPQuery;

    const whereClause =
      `id in (select bp.id from BusinessPartner bp join bp.mDMBPEstateList u ` +
      `where u.id in (select e.id from MDM_BPEstate e join e.mDMEstate es ` +
      `join es.mDMMeterPointList v where v.name = '${mpanName}'))`;

    try {
      const data = await executeGraphQLQuery(query, {
        where: whereClause,
      });

      if (data.businessPartner && data.businessPartner.length > 0) {
        return data.businessPartner[0];
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch business partner data:', error);
      return null;
    }
  }, []);

  /**
   * Fetches organization data from 360 for the supplier_reference.
   */
  const fetchOrganizationData = useCallback(async () => {
    const query = organizationsQuery;

    try {
      const data = await executeGraphQLQuery(query);

      if (data.getOrganizations && data.getOrganizations.length > 0) {
        return data.getOrganizations[0];
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch organization data:', error);
      return null;
    }
  }, []);

  /**
   * Builds the full JSON payload for BOP.Dataflows.StartGain queue.
   */
  const buildPayload = (formData, bpData, orgData) => {
    const mpanName = formData.mpan_core;
    const isDomestic = domesticIndicator;
    const isInitialReg = selectedMeterPoint
      ? getIsInitialRegistration(selectedMeterPoint.mDMEstateSupplyList)
      : true;

    // Contact details from BP
    const primaryContact = bpData ? findPrimaryContact(bpData.cRMBpartnerContactList) : null;
    const bpAddress = bpData && bpData.mdmContactAddress && bpData.mdmContactAddress.locationAddress
      ? bpData.mdmContactAddress.locationAddress
      : null;
    const customerName = bpData ? (bpData.mdmCompanyName || bpData.name) : null;
    const supplierReference = orgData ? orgData.organizationId : null;

    const addressFields = mapContactAddress(bpAddress);

    const contacts = [];
    if (primaryContact) {
      const contactEntry = {
        contact_name: `${primaryContact.firstName || ''} ${primaryContact.lastName || ''}`.trim() || null,
        telephones: primaryContact.phone
          ? [{ telephone_number: primaryContact.phone }]
          : [],
        emails: primaryContact.email
          ? [{ email_address: primaryContact.email }]
          : [],
      };
      contacts.push(contactEntry);
    }

    const payload = {
      supply_start_date: new Date(formData.supply_start_date + 'T00:00:00Z').toISOString(),
      ofaf_ref: null,
      mpan_core: parseInt(mpanName, 10),
      domestic_indicator: isDomestic,
      is_initial_registration: isInitialReg,
      change_of_occupancy_indicator: formData.change_of_occupancy_indicator || false,
      erroneous_switch_resolution_indicator: formData.erroneous_switch_resolution_indicator || false,
      supplier_reference: supplierReference,
      ms_appointment_request: {
        metering_service_mpid: formData.metering_service_mpid,
        contract_reference: formData.ms_contract_reference || null,
      },
      ds_appointment_request: {
        data_service_mpid: formData.data_service_mpid,
        contract_reference: formData.ds_contract_reference || null,
        mdr_mpid: formData.mdr_mpid || null,
        consent_granularity: formData.consent_granularity,
        fall_back_read_frequency: formData.fall_back_read_frequency,
      },
      contact_details: [
        {
          customer_name: customerName,
          ...addressFields,
          contacts: contacts,
        },
      ],
    };

    return payload;
  };

  const handleSubmit = async ({ formData }) => {
    setIsLoading(true);
    try {
      // Fetch meter point probe + business partner data + organization data from 360
      const [meterPointProbe, bpData, orgData] = await Promise.all([
        fetchMeterPointData(formData.mpan_core),
        fetchBPData(formData.mpan_core),
        fetchOrganizationData(),
      ]);

      if (!meterPointProbe) {
        console.warn('No OB_meterPoint found for MPAN:', formData.mpan_core);
      }

      if (!bpData) {
        console.warn('No business partner found for MPAN:', formData.mpan_core);
      }

      const payload = buildPayload(formData, bpData, orgData);

      const response = await axios.post('/publishMessage', payload, {
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
        },
        params: { queue: 'BOP.Dataflows.StartGain' },
      });

      setIsLoading(false);

      if (response.data === 'Message sent') {
        ReactDOM.render(
          <ExpiringAlert color='success' message='Change of Supplier request submitted successfully!' />,
          document.getElementById('alert').appendChild(document.createElement('div')),
        );
        onClose();
      } else {
        ReactDOM.render(
          <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
          document.getElementById('alert').appendChild(document.createElement('div')),
        );
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error submitting Change of Supplier request:', error);
      ReactDOM.render(
        <ExpiringAlert color='danger' message='Error submitting request. Please check the console for details.' />,
        document.getElementById('alert').appendChild(document.createElement('div')),
      );
    }
  };

  const uiSchemaWithWidgets = useMemo(
    () => ({
      ...changeOfSupplierUISchema,
      mpan_core: {
        ...changeOfSupplierUISchema.mpan_core,
        'ui:widget': (props) => (
          <MeterPointSelector
            {...props}
            placeholder='Select MPAN (non-Live status only)'
            excludeStatusFilter='L'
            onMeterPointSelect={handleMeterPointSelect}
          />
        ),
      },
    }),
    [handleMeterPointSelect],
  );

  const loadingIcon = isLoading ? (
    <span>
      <i className='fa fa-circle-o-notch fa-spin'></i>{' '}
    </span>
  ) : null;

  return (
    <div className='change-of-supplier-form'>
      <Form
        schema={changeOfSupplierSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        validate={customValidate}
        showErrorList={false}
        onSubmit={handleSubmit}
        onChange={handleChange}
      >
        {selectedMeterPoint && (
          <div className='alert alert-info mt-2 mb-3'>
            <strong>Meter Point Info:</strong>{' '}
            Market Sector: {selectedMeterPoint.marketSector || 'N/A'} |{' '}
            Domestic: {domesticIndicator === true ? 'Yes' : 'No'} |{' '}
            Min working days for date: {domesticIndicator === true ? '1' : '2'}
          </div>
        )}
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && loadingIcon}Submit Request
          </Button>
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChangeOfSupplierForm;
